/** @format */

import { makeStyles } from "@material-ui/styles";
import React from "react";
import _ from "lodash";
import { ClosePeriodType, IPlannerMinute, ITripPlanner, ServicedBy } from "../types/plannerTypes";
import { calcClosedPeriod, convertTZ, currentTimePositionLeft, prepareTimelineContent } from "./plannerHelpers";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { IPlannerState } from "../../../store/modules/plannerSlice";
import PlannerTripDetails from "./PlannerTripDetails";
import moment from "moment";

interface PlannerTimelineLayersProps {
  selectedDay: string;
  plannerStore: IPlannerState;
  tripPlanner: ITripPlanner;
  blockLabel: string | null;
  timelineContent: IPlannerMinute[];
  setTimelineContent: any;
  applyTimelineChanges: any;
  setErrorMessage:any
}

const timelineHeight = 90;
const hourLabelHeight = 30;

const useStyles = makeStyles((theme) => ({
  crossStrip: {
    background: "repeating-linear-gradient(-45deg, #00152E, #00152E 2px, transparent 2px, transparent 12px)",
    height: timelineHeight,
    position: "relative",
    top: -timelineHeight,
    marginBottom: -timelineHeight,
  },
  cusCrossStrip: {
    background: "repeating-linear-gradient(-45deg, #11031c, #11031c 2px, transparent 2px, transparent 12px)",
    height: timelineHeight,
    position: "relative",
    top: -timelineHeight,
    marginBottom: -timelineHeight,
  },
  blockLabel: {
    position: "absolute",
    zIndex: 1200,
    top: 4,
    left: 4,
    backgroundColor: "#00152E",
    color: "#02489D",
    fontWeight: 700,
    fontSize: "17px",
    padding: "1px 6px",
    borderRadius: "15px",
  },
  cusBlockLabel: {
    position: "absolute",
    zIndex: 1200,
    top: 4,
    left: 4,
    backgroundColor: "#11031c",
    color: "#552D7C",
    fontWeight: 700,
    fontSize: "17px",
    padding: "1px 6px",
    borderRadius: "15px",
  },
  yellowBorder: {
    height: timelineHeight - 6,
    backgroundColor: "transparent",
    border: "3px solid #F4B827",
    position: "absolute",
    zIndex: 700,
    top: 0,
  },
}));

const CurrentTimeGreenLine = ({ positionLeft }: { positionLeft: number }) => {
  return (
    <div
      style={{
        width: 3,
        height: timelineHeight,
        backgroundColor: "#3FA052",
        position: "absolute",
        zIndex: 700,
        top: 0,
        left: positionLeft,
      }}
    ></div>
  );
};

const PlannerTimelineLayers: React.FC<PlannerTimelineLayersProps> = (props: any) => {
  const { selectedDay, tripPlanner, plannerStore, blockLabel, timelineContent, setTimelineContent, applyTimelineChanges,setErrorMessage } = props;
  const [isTodaySelected, setIsTodaySelected] = React.useState(moment().isSame(selectedDay, "day"));
  const classes = useStyles();
  const [minuteWidth, seMinuteWidth] = React.useState<number>(0);
  const [quarterMinuteWidth, setQuarterMinuteWidth] = React.useState<number>(0);
  const [hourWidth, setHourWidth] = React.useState<number>(0);

  const [closedStartPeriodWidth, setClosedStartPeriodWidth] = React.useState(0);
  const [closedEndPeriodWidth, setClosedEndPeriodWidth] = React.useState(0);

  const { plannerYellowBorderStore } = useSelector((state: RootState) => state);
  const [currentTimeWidth, setCurrentTimeWidth] = React.useState(0);

  React.useEffect(() => {
    if (isTodaySelected) {
      localStorage.setItem("Timezone",tripPlanner.timezone);
      setCurrentTimeWidth(currentTimePositionLeft(minuteWidth, quarterMinuteWidth, hourWidth, timelineContent, convertTZ(new Date(),tripPlanner.timezone)));
      const interval = setInterval(() => {
        setCurrentTimeWidth(currentTimePositionLeft(minuteWidth, quarterMinuteWidth, hourWidth, timelineContent, convertTZ(new Date(),tripPlanner.timezone)));
        const ts =new Date;              
        setTimelineContent(
          timelineContent.map((tc: any) => {
            return {
              ...tc,
              isPassed: tc.date <= ts,
            };
          })
        );
      }, 60 * 1000); // update every minute
      return () => {
        clearInterval(interval);
        setCurrentTimeWidth(0)
      }
    }
  }, [minuteWidth, quarterMinuteWidth, hourWidth, timelineContent]);
  React.useEffect(() => {
    setIsTodaySelected(moment().isSame(selectedDay, "day"))
  }, [selectedDay]);

  const calculateMinuteWidth = React.useCallback(() => {
    if (timelineContent.length > 0) {
      const hrsNumber = timelineContent.filter((i: any) =>i.date.getMinutes() % 60 === 0).length;
      const windowWidth = document.body.clientWidth;
      const timelineBlockWidth = (windowWidth - hrsNumber) / timelineContent.length;
      return timelineBlockWidth;
    }
    return 0;
  }, [timelineContent]);

  React.useEffect(() => {
    const blockWidth = calculateMinuteWidth();
    seMinuteWidth(blockWidth);
    setQuarterMinuteWidth(blockWidth);
    setHourWidth(blockWidth + 1);
  }, [timelineContent]);

  React.useEffect(() => {
    const debouncedWidthCalc = _.debounce(function handleResize() {
      const blockWidth = calculateMinuteWidth();
      seMinuteWidth(blockWidth);
      setQuarterMinuteWidth(blockWidth);
      setHourWidth(blockWidth + 1);
    }, 1000);

    window.addEventListener("resize", debouncedWidthCalc);

    return () => window.removeEventListener("resize", debouncedWidthCalc);
  });

  React.useEffect(() => {
    const timelineVisualState = plannerStore.visualState.filter((v: any) => v.tripPlannerId == tripPlanner.tripPlannerId);
    setTimelineContent(prepareTimelineContent(tripPlanner, selectedDay, timelineVisualState, plannerStore.localState));
  }, [tripPlanner, selectedDay, plannerStore.visualState]);

  React.useEffect(() => {
    if (minuteWidth && quarterMinuteWidth && hourWidth) {
      setClosedStartPeriodWidth(calcClosedPeriod(minuteWidth, quarterMinuteWidth, hourWidth, ClosePeriodType.start, timelineContent, tripPlanner));
      setClosedEndPeriodWidth(calcClosedPeriod(minuteWidth, quarterMinuteWidth, hourWidth, ClosePeriodType.end, timelineContent, tripPlanner));
    }
  }, [minuteWidth, quarterMinuteWidth, hourWidth, timelineContent]);

  const leadTimeWidth = isTodaySelected ? ((tripPlanner.schedulingLeadTtime || 0) * minuteWidth) / 5 : 0;
  let closedStartPeriodWidthCalc =
    closedStartPeriodWidth > currentTimeWidth + leadTimeWidth ? closedStartPeriodWidth : currentTimeWidth + leadTimeWidth;

  if (closedStartPeriodWidthCalc > document.body.clientWidth || !tripPlanner.open) {
    closedStartPeriodWidthCalc = document.body.clientWidth;
  }
  return (
    <>
      {isTodaySelected && currentTimeWidth < document.body.clientWidth && <CurrentTimeGreenLine positionLeft={currentTimeWidth} />}
      {blockLabel && tripPlanner.servicedBy==ServicedBy.SMA && <div className={classes.blockLabel}>{blockLabel}</div>}
      {blockLabel && tripPlanner.servicedBy==ServicedBy.Customer && <div className={classes.cusBlockLabel}>{blockLabel}</div>}
      {tripPlanner.servicedBy==ServicedBy.SMA ?(<div
        className={classes.crossStrip}
        style={{
          width: closedStartPeriodWidthCalc,
        }}
      ></div>):(<div
        className={classes.cusCrossStrip}
        style={{
          width: closedStartPeriodWidthCalc,
        }}
      ></div>)}
      {currentTimeWidth < document.body.clientWidth && (
        <div
          className={classes.crossStrip}
          style={{
            width: closedEndPeriodWidth,
            marginRight: 0,
            marginLeft: "auto",
          }}
        ></div>
      )}
      {plannerYellowBorderStore.showYellowBorder && (
        <div
          className={classes.yellowBorder}
          style={{
            left: closedStartPeriodWidthCalc,
            width: document.body.clientWidth - closedStartPeriodWidthCalc - closedEndPeriodWidth - 6,
          }}
        />
      )}
      <PlannerTripDetails
        timelineContent={timelineContent}
        minuteWidth={minuteWidth}
        quarterMinuteWidth={quarterMinuteWidth}
        hourWidth={hourWidth}
        blockLabel={blockLabel}
        applyTimelineChanges={applyTimelineChanges}
        triplaner ={tripPlanner.sharingRole}
        setErrorMessage = {setErrorMessage}
      />
    </>
  );
};
export default React.memo(PlannerTimelineLayers);
